import React, { useState, useMemo } from 'react';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { CalendarIcon, Search, Download, BarChart3, TrendingUp, AlertTriangle, Check, ChevronDown, X } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useMultiProductBenchmarkData, useBenchmarkProducts } from '@/hooks/use-benchmark-data';
import { MultiProductBenchmarkFilters } from '@/services/benchmark/types';
import { LOCATION_GROUP_NAMES } from '@/services/benchmark/constants';
import * as XLSX from 'xlsx';

const MultiProductComparison: React.FC = () => {
  const [filters, setFilters] = useState<MultiProductBenchmarkFilters>({
    dateFrom: format(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    dateTo: format(new Date(), 'yyyy-MM-dd'),
    products: [],
    locationGroups: ['farm-prices', 'distribution-center'],
    frequency: 'daily',
    comparisonType: 'avg',
    maxProducts: 10,
  });

  const [isProductDropdownOpen, setIsProductDropdownOpen] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const [debugData, setDebugData] = useState<any>(null);
  
  const { data: availableProducts } = useBenchmarkProducts();
  const { data: benchmarkData, isLoading, error } = useMultiProductBenchmarkData(filters);

  const locationGroupOptions = [
    { value: 'farm-prices', label: 'Farm Prices' },
    { value: 'distribution-center', label: 'Distribution Center' },
    { value: 'local-shops', label: 'Local Shops' },
    { value: 'sunday-market', label: 'Sunday Market' },
    { value: 'supermarket', label: 'Supermarket' },
    { value: 'ecommerce', label: 'Ecommerce' },
    { value: 'chipchip-sales', label: 'ChipChip Sales' },
  ];

  const handleProductToggle = (product: string, checked: boolean) => {
    setFilters(prev => ({
      ...prev,
      products: checked 
        ? [...prev.products, product]
        : prev.products.filter(p => p !== product)
    }));
  };

  const handleRemoveProduct = (productToRemove: string) => {
    setFilters(prev => ({
      ...prev,
      products: prev.products.filter(p => p !== productToRemove)
    }));
  };

  const handleLocationGroupToggle = (locationGroup: string, checked: boolean) => {
    setFilters(prev => ({
      ...prev,
      locationGroups: checked 
        ? [...prev.locationGroups, locationGroup]
        : prev.locationGroups.filter(lg => lg !== locationGroup)
    }));
  };

  const handleDateChange = (field: 'dateFrom' | 'dateTo', date: Date | undefined) => {
    if (date) {
      setFilters(prev => ({
        ...prev,
        [field]: format(date, 'yyyy-MM-dd')
      }));
    }
  };

  const getMarketPositionColor = (position: 'low' | 'medium' | 'high') => {
    switch (position) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const exportChipChipData = () => {
    if (!benchmarkData) return;

    const exportData: any[] = [];
    
    benchmarkData.products.forEach(product => {
      const chipchipGroup = product.locationGroups.find(group => group.groupName === 'ChipChip Sales');
      const otherGroups = product.locationGroups.filter(group => group.groupName !== 'ChipChip Sales');
      
      if (!chipchipGroup || otherGroups.length === 0) return;

      chipchipGroup.dates.forEach((date, index) => {
        const chipchipPrice = chipchipGroup.avgPrices[index];
        
        const baseRow = {
          Product: product.productName,
          Date: format(new Date(date), 'yyyy-MM-dd'),
          'ChipChip Price (ETB/kg)': chipchipPrice.toFixed(2)
        };

        otherGroups.forEach(group => {
          const dateIndex = group.dates.indexOf(date);
          if (dateIndex !== -1) {
            const marketPrice = group.avgPrices[dateIndex];
            const priceDifference = marketPrice - chipchipPrice;
            const percentageDown = marketPrice > 0 ? ((priceDifference / marketPrice) * 100) : 0;
            
            baseRow[`${group.groupName} Price (ETB/kg)`] = marketPrice.toFixed(2);
            baseRow[`${group.groupName} Price Difference (ETB)`] = priceDifference.toFixed(2);
            baseRow[`${group.groupName} Price Down (%)`] = percentageDown.toFixed(1);
          }
        });

        exportData.push(baseRow);
      });
    });

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);
    
    // Auto-size columns
    const colWidths = [];
    const headers = Object.keys(exportData[0] || {});
    headers.forEach((header, i) => {
      const maxLength = Math.max(
        header.length,
        ...exportData.map(row => String(row[header] || '').length)
      );
      colWidths[i] = { wch: Math.min(maxLength + 2, 30) };
    });
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, 'ChipChip Price Comparison');
    
    // Generate filename with current date
    const filename = `ChipChip_Price_Down_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Multi-Product Price Comparison</h1>
            <p className="text-muted-foreground">
              Compare prices across multiple products and market sources
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setDebugMode(!debugMode)}
              className={debugMode ? "bg-yellow-100" : ""}
            >
              {debugMode ? "Hide Debug" : "Show Debug"}
            </Button>
            <Button disabled={!benchmarkData} className="gap-2" onClick={exportChipChipData}>
              <Download className="h-4 w-4" />
              Export ChipChip Data
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Filters Sidebar */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Filters</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Date Range */}
                <div className="space-y-4">
                  <Label className="text-sm font-medium">Date Range</Label>
                  <div className="grid grid-cols-1 gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "justify-start text-left font-normal",
                            !filters.dateFrom && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {filters.dateFrom ? format(new Date(filters.dateFrom), "PPP") : "From date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={new Date(filters.dateFrom)}
                          onSelect={(date) => handleDateChange('dateFrom', date)}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "justify-start text-left font-normal",
                            !filters.dateTo && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {filters.dateTo ? format(new Date(filters.dateTo), "PPP") : "To date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={new Date(filters.dateTo)}
                          onSelect={(date) => handleDateChange('dateTo', date)}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                {/* Product Selection */}
                <div className="space-y-4">
                  <Label className="text-sm font-medium">
                    Products ({filters.products.length}/{filters.maxProducts})
                  </Label>
                  <Popover open={isProductDropdownOpen} onOpenChange={setIsProductDropdownOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={isProductDropdownOpen}
                        className="w-full justify-between text-left font-normal"
                      >
                        {filters.products.length === 0 
                          ? "Select products..." 
                          : `${filters.products.length} product${filters.products.length > 1 ? 's' : ''} selected`
                        }
                        <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0 bg-background border shadow-lg z-50">
                      <Command>
                        <CommandInput placeholder="Search products..." className="h-9" />
                        <CommandEmpty>No products found.</CommandEmpty>
                        <CommandList className="max-h-60">
                          <CommandGroup>
                            {availableProducts?.map((product) => (
                              <CommandItem
                                key={product}
                                value={product}
                                onSelect={() => {
                                  const isSelected = filters.products.includes(product);
                                  const canAdd = !isSelected && filters.products.length < (filters.maxProducts || 10);
                                  
                                  if (isSelected || canAdd) {
                                    handleProductToggle(product, !isSelected);
                                  }
                                }}
                                className="cursor-pointer"
                              >
                                <div className="flex items-center space-x-2 w-full">
                                  <div className="flex h-4 w-4 items-center justify-center">
                                    {filters.products.includes(product) && (
                                      <Check className="h-4 w-4" />
                                    )}
                                  </div>
                                  <span className="flex-1">{product}</span>
                                  {!filters.products.includes(product) && 
                                   filters.products.length >= (filters.maxProducts || 10) && (
                                    <span className="text-xs text-muted-foreground">Max reached</span>
                                  )}
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  
                  {/* Selected Products Display */}
                  {filters.products.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {filters.products.map((product) => (
                        <Badge 
                          key={product} 
                          variant="secondary" 
                          className="flex items-center gap-1 px-2 py-1"
                        >
                          <span className="text-xs">{product}</span>
                          <X 
                            className="h-3 w-3 cursor-pointer hover:text-destructive" 
                            onClick={() => handleRemoveProduct(product)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>

                {/* Location Groups */}
                <div className="space-y-4">
                  <Label className="text-sm font-medium">Market Sources</Label>
                  <div className="space-y-2">
                    {locationGroupOptions.map((option) => (
                      <div key={option.value} className="flex items-center space-x-2">
                        <Checkbox
                          id={option.value}
                          checked={filters.locationGroups.includes(option.value)}
                          onCheckedChange={(checked) => 
                            handleLocationGroupToggle(option.value, checked as boolean)
                          }
                        />
                        <Label
                          htmlFor={option.value}
                          className="text-sm font-normal cursor-pointer"
                        >
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Comparison Type */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Comparison Type</Label>
                  <Select
                    value={filters.comparisonType}
                    onValueChange={(value: 'min' | 'avg') => 
                      setFilters(prev => ({ ...prev, comparisonType: value }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="min">Minimum Prices</SelectItem>
                      <SelectItem value="avg">Average Prices</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {filters.products.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <BarChart3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-medium mb-2">Select Products to Compare</h3>
                  <p className="text-muted-foreground">
                    Choose multiple products from the sidebar to start comparing prices across different market sources.
                  </p>
                </CardContent>
              </Card>
            ) : isLoading ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading comparison data...</p>
                </CardContent>
              </Card>
            ) : error ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <AlertTriangle className="h-12 w-12 mx-auto mb-4 text-red-500" />
                  <h3 className="text-lg font-medium mb-2">Error Loading Data</h3>
                  <p className="text-muted-foreground">
                    Unable to load comparison data. Please try again.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Tabs defaultValue="overview" className="space-y-6">
                <TabsList>
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="detailed">Detailed Analysis</TabsTrigger>
                  <TabsTrigger value="chipchip-comparison">ChipChip Price Down</TabsTrigger>
                  <TabsTrigger value="opportunities">Opportunities</TabsTrigger>
                  {debugMode && <TabsTrigger value="debug">Debug</TabsTrigger>}
                </TabsList>

                <TabsContent value="overview" className="space-y-6">
                  {/* Product Comparison Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Product Comparison Overview</CardTitle>
                      <CardDescription>
                        Price comparison across selected products and market sources
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Product</TableHead>
                            <TableHead>Avg Price (ETB/kg)</TableHead>
                            <TableHead>Price Range</TableHead>
                            <TableHead>Market Position</TableHead>
                            <TableHead>Volatility</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {benchmarkData?.products.map((product) => (
                            <TableRow key={product.productName}>
                              <TableCell className="font-medium">
                                {product.productName}
                              </TableCell>
                              <TableCell>
                                {product.averagePrice.toFixed(2)}
                              </TableCell>
                              <TableCell>
                                {product.priceRange.min.toFixed(2)} - {product.priceRange.max.toFixed(2)}
                              </TableCell>
                              <TableCell>
                                <Badge 
                                  className={getMarketPositionColor(
                                    benchmarkData.crossProductAnalysis.marketPositioning[product.productName]
                                  )}
                                >
                                  {benchmarkData.crossProductAnalysis.marketPositioning[product.productName]}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <span className={product.volatilityScore > 20 ? 'text-red-600' : 'text-green-600'}>
                                  {product.volatilityScore.toFixed(1)}%
                                </span>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="detailed" className="space-y-6">
                  {benchmarkData?.products.map((product) => (
                    <Card key={product.productName}>
                      <CardHeader>
                        <CardTitle>{product.productName} - Market Analysis</CardTitle>
                        <CardDescription>
                          Detailed price analysis across {product.locationGroups.length} market sources
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Market Source</TableHead>
                              <TableHead>Avg Price (ETB/kg)</TableHead>
                              <TableHead>Price Range</TableHead>
                              <TableHead>Data Points</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {product.locationGroups.map((group) => {
                              const avgPrice = group.avgPrices.length > 0 
                                ? group.avgPrices.reduce((a, b) => a + b, 0) / group.avgPrices.length 
                                : 0;
                              const minPrice = group.minPrices.length > 0 ? Math.min(...group.minPrices) : 0;
                              const maxPrice = group.avgPrices.length > 0 ? Math.max(...group.avgPrices) : 0;
                              
                              return (
                                <TableRow key={group.groupName}>
                                  <TableCell className="font-medium">
                                    {group.groupName}
                                  </TableCell>
                                  <TableCell>{avgPrice.toFixed(2)}</TableCell>
                                  <TableCell>
                                    {minPrice.toFixed(2)} - {maxPrice.toFixed(2)}
                                  </TableCell>
                                  <TableCell>{group.dates.length}</TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </CardContent>
                    </Card>
                  ))}
                </TabsContent>

                <TabsContent value="chipchip-comparison" className="space-y-6">
                  {benchmarkData?.products.map((product) => {
                    // Debug logging
                    console.log(`Product: ${product.productName}`);
                    console.log('Available location groups:', product.locationGroups.map(g => g.groupName));
                    
                    const chipchipGroup = product.locationGroups.find(group => group.groupName === 'ChipChip Sales');
                    const otherGroups = product.locationGroups.filter(group => group.groupName !== 'ChipChip Sales');
                    
                    console.log('ChipChip group found:', !!chipchipGroup);
                    console.log('Other groups count:', otherGroups.length);
                    console.log('Other group names:', otherGroups.map(g => g.groupName));
                    
                    // More specific error handling
                    if (!chipchipGroup && otherGroups.length === 0) {
                      return (
                        <Card key={product.productName}>
                          <CardHeader>
                            <CardTitle>{product.productName} - ChipChip Price Comparison</CardTitle>
                            <CardDescription>
                              No market data available for this product
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <p className="text-muted-foreground text-center py-8">
                              No market data found for {product.productName}. Make sure to select market sources like "Farm Prices", "Distribution Center", etc. along with "ChipChip Sales".
                            </p>
                          </CardContent>
                        </Card>
                      );
                    }
                    
                    if (!chipchipGroup) {
                      return (
                        <Card key={product.productName}>
                          <CardHeader>
                            <CardTitle>{product.productName} - ChipChip Price Comparison</CardTitle>
                            <CardDescription>
                              ChipChip data not available for this product
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <p className="text-muted-foreground text-center py-8">
                              No ChipChip data found for {product.productName}. Make sure "ChipChip Sales" is selected in the market sources filter.
                            </p>
                          </CardContent>
                        </Card>
                      );
                    }
                    
                    if (otherGroups.length === 0) {
                      return (
                        <Card key={product.productName}>
                          <CardHeader>
                            <CardTitle>{product.productName} - ChipChip Price Comparison</CardTitle>
                            <CardDescription>
                              No other market data available for comparison
                            </CardDescription>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-4">
                              <p className="text-muted-foreground text-center py-4">
                                ChipChip data is available for {product.productName}, but no other market data found for the selected date range and filters.
                              </p>
                              <div className="bg-muted p-4 rounded-lg">
                                <h4 className="font-medium mb-2">Possible solutions:</h4>
                                <ul className="text-sm text-muted-foreground space-y-1">
                                  <li>• Try expanding the date range to include more data</li>
                                  <li>• Check if "{product.productName}" has data in other market sources for this period</li>
                                  <li>• Ensure market sources like "Farm Prices", "Distribution Center", etc. are selected</li>
                                  <li>• Product names must match exactly across different data sources</li>
                                </ul>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    }

                    // Create comparison data by date
                    const comparisonData = chipchipGroup.dates.map((date, index) => {
                      const chipchipPrice = chipchipGroup.avgPrices[index];
                      const marketComparisons = otherGroups.map(group => {
                        const dateIndex = group.dates.indexOf(date);
                        if (dateIndex === -1) return null;
                        
                        const marketPrice = group.avgPrices[dateIndex];
                        const priceDifference = marketPrice - chipchipPrice;
                        const percentageDown = marketPrice > 0 ? ((priceDifference / marketPrice) * 100) : 0;
                        
                        return {
                          marketName: group.groupName,
                          marketPrice,
                          priceDifference,
                          percentageDown
                        };
                      }).filter(Boolean);

                      return {
                        date,
                        chipchipPrice,
                        marketComparisons
                      };
                    });

                    return (
                      <Card key={product.productName}>
                        <CardHeader>
                          <CardTitle>{product.productName} - ChipChip Price Down Analysis</CardTitle>
                          <CardDescription>
                            How much cheaper ChipChip is compared to other markets by date
                          </CardDescription>
                        </CardHeader>
                        <CardContent>
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Date</TableHead>
                                <TableHead>ChipChip Price (ETB/kg)</TableHead>
                                {otherGroups.map(group => (
                                  <React.Fragment key={group.groupName}>
                                    <TableHead>{group.groupName} Price</TableHead>
                                    <TableHead>{group.groupName} Price Down</TableHead>
                                  </React.Fragment>
                                ))}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {comparisonData.map((dayData) => (
                                <TableRow key={dayData.date}>
                                  <TableCell className="font-medium">
                                    {format(new Date(dayData.date), 'MMM dd, yyyy')}
                                  </TableCell>
                                  <TableCell>
                                    {dayData.chipchipPrice.toFixed(2)}
                                  </TableCell>
                                  {otherGroups.map(group => {
                                    const comparison = dayData.marketComparisons.find(c => c?.marketName === group.groupName);
                                    return (
                                      <React.Fragment key={group.groupName}>
                                        <TableCell>
                                          {comparison ? comparison.marketPrice.toFixed(2) : 'N/A'}
                                        </TableCell>
                                        <TableCell>
                                          {comparison ? (
                                            <div className="flex items-center gap-1">
                                              <span className={comparison.priceDifference > 0 ? 'text-green-600' : 'text-red-600'}>
                                                {comparison.priceDifference > 0 ? '-' : '+'}{Math.abs(comparison.priceDifference).toFixed(2)} ETB
                                              </span>
                                              <span className="text-xs text-muted-foreground">
                                                ({comparison.percentageDown > 0 ? '-' : '+'}{Math.abs(comparison.percentageDown).toFixed(1)}%)
                                              </span>
                                            </div>
                                          ) : 'N/A'}
                                        </TableCell>
                                      </React.Fragment>
                                    );
                                  })}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </CardContent>
                      </Card>
                    );
                  })}
                </TabsContent>

                <TabsContent value="opportunities" className="space-y-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="h-5 w-5" />
                        Arbitrage Opportunities
                      </CardTitle>
                      <CardDescription>
                        Potential pricing opportunities between products
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {benchmarkData?.crossProductAnalysis.arbitrageOpportunities.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">
                          No significant arbitrage opportunities found between selected products.
                        </p>
                      ) : (
                        <div className="space-y-4">
                          {benchmarkData?.crossProductAnalysis.arbitrageOpportunities.map((opportunity, index) => (
                            <div
                              key={index}
                              className="p-4 rounded-lg border bg-green-50 border-green-200"
                            >
                              <div className="flex items-start justify-between">
                                <div>
                                  <h4 className="font-medium text-green-900">
                                    {opportunity.productA} vs {opportunity.productB}
                                  </h4>
                                  <p className="text-green-700 mt-1">
                                    {opportunity.opportunity}
                                  </p>
                                </div>
                                <Badge variant="secondary" className="bg-green-100 text-green-800">
                                  {opportunity.priceDifference.toFixed(2)} ETB/kg
                                </Badge>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {debugMode && (
                  <TabsContent value="debug" className="space-y-6">
                    <Card>
                      <CardHeader>
                        <CardTitle>Debug Information</CardTitle>
                        <CardDescription>
                          Technical details about API calls and data processing
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-4">
                          <div>
                            <h4 className="font-medium mb-2">Request Filters</h4>
                            <div className="bg-muted p-3 rounded text-sm">
                              <pre>{JSON.stringify(filters, null, 2)}</pre>
                            </div>
                          </div>
                          
                          <div>
                            <h4 className="font-medium mb-2">Processing Status</h4>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <p><span className="font-medium">Selected Products:</span> {filters.products.length}</p>
                                <p><span className="font-medium">Selected Markets:</span> {filters.locationGroups.length}</p>
                                <p><span className="font-medium">Date Range:</span> {filters.dateFrom} to {filters.dateTo}</p>
                                <p><span className="font-medium">ChipChip Included:</span> {filters.locationGroups.includes('chipchip-sales') ? 'Yes' : 'No'}</p>
                              </div>
                              <div className="space-y-2">
                                <p><span className="font-medium">Data Loading:</span> {isLoading ? 'Loading...' : 'Complete'}</p>
                                <p><span className="font-medium">Error Status:</span> {error ? 'Error' : 'None'}</p>
                                <p><span className="font-medium">Products Found:</span> {benchmarkData?.products.length || 0}</p>
                              </div>
                            </div>
                          </div>

                          {benchmarkData?.products && (
                            <div>
                              <h4 className="font-medium mb-2">Product Data Summary</h4>
                              <div className="space-y-3">
                                {benchmarkData.products.map((product) => (
                                  <div key={product.productName} className="border rounded p-3">
                                    <h5 className="font-medium">{product.productName}</h5>
                                    <div className="mt-2 text-sm space-y-1">
                                      <p><span className="font-medium">Market Sources:</span> {product.locationGroups.length}</p>
                                      <div className="ml-2">
                                        {product.locationGroups.map((group) => (
                                          <p key={group.groupName}>
                                            • {group.groupName}: {group.dates.length} data points
                                            {group.groupName === 'ChipChip Sales' && (
                                              <span className="text-green-600 ml-2">✓ ChipChip data found</span>
                                            )}
                                          </p>
                                        ))}
                                      </div>
                                      <p><span className="font-medium">Date Range:</span> {product.allDates.length} unique dates</p>
                                      <p><span className="font-medium">Price Range:</span> {product.priceRange.min.toFixed(2)} - {product.priceRange.max.toFixed(2)} ETB/kg</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div>
                            <h4 className="font-medium mb-2">Troubleshooting Tips</h4>
                            <div className="bg-blue-50 p-3 rounded text-sm">
                              <ul className="space-y-1">
                                <li>• Check browser console for detailed API logs (F12 → Console)</li>
                                <li>• Verify product names match exactly between ChipChip and other sources</li>
                                <li>• Try expanding the date range if no data is found</li>
                                <li>• Ensure ChipChip Sales is selected along with other market sources</li>
                                <li>• Product variations are automatically handled (e.g., "Tomato" → "Tomato A")</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>
                )}
              </Tabs>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default MultiProductComparison;